<template>
  <Teleport to="body">
    <div
      v-if="sheetOpen"
      ref="rootEl"
      class="fixed inset-0 z-[120] flex justify-center bg-black/85 p-4 font-sans text-white"
      :class="state.phase === 'edit' ? 'items-start pt-[8vh]' : 'items-center'"
      @dblclick.stop.prevent
      @click.stop
    >
      <div
        class="w-full max-w-md rounded-xl border border-white/15 bg-neutral-900 p-4 shadow-2xl"
        :role="state.phase === 'error' ? 'alertdialog' : 'dialog'"
        aria-modal="true"
        :aria-labelledby="titleId"
      >
        <div class="flex items-start justify-between gap-3">
          <h2
            :id="titleId"
            class="text-[15px] font-semibold"
          >
            {{ wifiText('sheet.title') }}
          </h2>
          <span
            v-if="unlockLine"
            class="shrink-0 rounded-full border border-white/15 px-2 py-0.5 text-[10px] text-white/70"
          >
            {{ unlockLine }}
          </span>
        </div>

        <div
          v-if="state.phase === 'status'"
          class="mt-4 flex flex-col gap-3"
        >
          <section class="rounded-lg border border-white/15 bg-white/[0.03] p-3">
            <p class="text-[10px] uppercase tracking-wide text-white/50">
              {{ wifiText('sheet.current') }}
            </p>
            <p
              v-if="statusSnap?.connectionType === 'ethernet'"
              class="mt-1 text-[13px] leading-snug"
            >
              {{ wifiText('sheet.ethernet') }}
            </p>
            <template v-else-if="statusSnap?.currentSsid">
              <p class="mt-1 text-[15px] font-semibold">
                {{ statusSnap.currentSsid }}
              </p>
              <p class="mt-0.5 text-[12px] text-white/70">
                {{ connectedLine }}
              </p>
            </template>
            <p
              v-else-if="state.savedSsid"
              class="mt-1 text-[15px] font-semibold"
            >
              {{ state.savedSsid }}
            </p>
            <p
              v-else
              class="mt-1 text-[13px] text-white/45"
            >
              —
            </p>
          </section>

          <section class="rounded-lg border border-white/15 bg-white/[0.03] p-3">
            <div class="flex items-center justify-between gap-2">
              <p class="text-[13px] font-semibold">
                {{ state.savedSsid ? wifiText('sheet.restoreOn') : wifiText('sheet.restoreOff') }}
              </p>
              <span
                v-if="state.savedSsid"
                class="shrink-0 rounded-full border border-emerald-400/35 bg-emerald-500/20 px-2 py-0.5 text-[10px] font-medium text-emerald-200"
              >
                {{ wifiText('sheet.pskSaved') }}
              </span>
            </div>
            <p
              v-if="state.savedSsid"
              class="mt-2 text-[12px] leading-snug text-white/70"
            >
              {{ wifiText('sheet.restoreHelp') }}
            </p>
          </section>

          <button
            class="w-full rounded-md border border-emerald-400/35 bg-emerald-500/20 py-2.5 text-[13px] font-semibold text-emerald-200 focus:outline-none focus:ring-2 focus:ring-white/80"
            type="button"
            data-wifi-focus
            data-wifi-primary
            @click="beginEdit"
          >
            {{ state.savedSsid ? wifiText('action.change') : wifiText('action.setup') }}
          </button>
          <button
            v-if="state.savedSsid"
            class="w-full rounded-md border border-white/15 py-2.5 text-[12px] font-medium text-white/80 focus:outline-none focus:ring-2 focus:ring-white/80"
            type="button"
            data-wifi-focus
            @click="askForget"
          >
            {{ wifiText('action.forget') }}
          </button>
          <button
            class="w-full rounded-md border border-white/15 py-2.5 text-[12px] font-medium text-white/70 focus:outline-none focus:ring-2 focus:ring-white/80"
            type="button"
            data-wifi-focus
            @click="openAndroidSettings"
          >
            {{ wifiText('action.androidSettings') }}
          </button>
        </div>

        <form
          v-else-if="state.phase === 'edit'"
          class="mt-4 flex flex-col gap-3"
          @submit.prevent="submit"
        >
          <label class="flex flex-col gap-1">
            <span class="text-[12px] font-medium">{{ wifiText('form.ssid') }}</span>
            <input
              class="rounded-md border border-white/15 bg-black/40 px-3 py-2 text-[14px] text-white focus:outline-none focus:ring-2 focus:ring-white/80"
              type="text"
              name="ssid"
              autocomplete="off"
              autocapitalize="off"
              autocorrect="off"
              spellcheck="false"
              enterkeyhint="next"
              data-wifi-focus
              :value="state.ssidDraft"
              @input="setSsid(($event.target as HTMLInputElement).value)"
            >
            <span class="text-[11px] text-white/50">{{ wifiText('form.ssidHelp') }}</span>
            <span
              v-if="ssidError"
              class="text-[11px] text-red-300"
            >{{ wifiText('form.ssidInvalid') }}</span>
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-[12px] font-medium">{{ wifiText('form.psk') }}</span>
            <div class="flex gap-2">
              <input
                class="min-w-0 flex-1 rounded-md border border-white/15 bg-black/40 px-3 py-2 text-[14px] text-white focus:outline-none focus:ring-2 focus:ring-white/80"
                :type="state.showPsk ? 'text' : 'password'"
                name="psk"
                autocomplete="off"
                enterkeyhint="done"
                data-wifi-focus
                :value="state.pskDraft"
                @input="setPsk(($event.target as HTMLInputElement).value)"
              >
              <button
                class="rounded-md border border-white/15 px-3 text-[11px] text-white/80 focus:outline-none focus:ring-2 focus:ring-white/80"
                type="button"
                data-wifi-focus
                @click="toggleShowPsk"
              >
                {{ state.showPsk ? wifiText('form.hide') : wifiText('form.show') }}
              </button>
            </div>
            <span class="text-[11px] text-white/50">{{ pskCounter }} · {{ wifiText('form.pskHelp') }}</span>
            <span
              v-if="pskError"
              class="text-[11px] text-red-300"
            >{{ wifiText('form.pskInvalid') }}</span>
          </label>
          <p class="text-[11px] leading-snug text-white/55">
            {{ wifiText('form.local') }}
          </p>
          <button
            class="w-full rounded-md border border-emerald-400/35 bg-emerald-500/20 py-2.5 text-[13px] font-semibold text-emerald-200 focus:outline-none focus:ring-2 focus:ring-white/80"
            type="submit"
            data-wifi-focus
            data-wifi-primary
          >
            {{ wifiText('form.submit') }}
          </button>
        </form>

        <div
          v-else-if="state.phase === 'applying'"
          class="mt-4"
        >
          <p class="text-[14px] font-semibold">
            {{ wifiText('apply.title', { ssid: state.ssidDraft }) }}
          </p>
          <ol class="mt-3 flex flex-col gap-2">
            <li
              v-for="(label, index) in applySteps"
              :key="label"
              class="text-[12px]"
              :class="index < state.applyStep ? 'text-emerald-200' : index === state.applyStep ? 'text-white' : 'text-white/35'"
            >
              {{ label }}
            </li>
          </ol>
        </div>

        <div
          v-else-if="state.phase === 'error'"
          class="mt-4 flex flex-col gap-3"
        >
          <p class="text-[14px] font-semibold text-red-300">
            {{ errorTitle }}
          </p>
          <p
            v-if="unchangedLine"
            class="text-[12px] leading-snug text-white/70"
          >
            {{ unchangedLine }}
          </p>
          <button
            class="w-full rounded-md border border-emerald-400/35 bg-emerald-500/20 py-2.5 text-[13px] font-semibold text-emerald-200 focus:outline-none focus:ring-2 focus:ring-white/80"
            type="button"
            data-wifi-focus
            data-wifi-primary
            @click="backToEdit"
          >
            {{ wifiText('error.retry') }}
          </button>
        </div>

        <div
          v-else-if="state.phase === 'success'"
          class="mt-4 flex flex-col gap-3"
        >
          <p class="text-[14px] font-semibold text-emerald-200">
            {{ wifiText('success.title', { ssid: state.savedSsid ?? '' }) }}
          </p>
          <p class="text-[12px] leading-snug text-white/70">
            {{ wifiText('success.body') }}
          </p>
          <p class="text-[12px] text-amber-200/90">
            {{ wifiText('success.closes', { s: successSeconds }) }}
          </p>
          <button
            class="w-full rounded-md border border-emerald-400/35 bg-emerald-500/20 py-2.5 text-[13px] font-semibold text-emerald-200 focus:outline-none focus:ring-2 focus:ring-white/80"
            type="button"
            data-wifi-focus
            data-wifi-primary
            @click="closeSheet"
          >
            {{ wifiText('success.done') }}
          </button>
        </div>

        <div
          v-else-if="state.phase === 'forgetConfirm'"
          class="mt-4 flex flex-col gap-3"
        >
          <p class="text-[13px] leading-snug">
            {{ wifiText('action.forgetConfirm', { ssid: state.savedSsid ?? '' }) }}
          </p>
          <button
            class="w-full rounded-md border border-emerald-400/35 bg-emerald-500/20 py-2.5 text-[13px] font-semibold text-emerald-200 focus:outline-none focus:ring-2 focus:ring-white/80"
            type="button"
            data-wifi-focus
            data-wifi-primary
            @click="confirmForget"
          >
            {{ wifiText('action.forget') }}
          </button>
          <button
            class="w-full rounded-md border border-white/15 py-2.5 text-[12px] font-medium text-white/70 focus:outline-none focus:ring-2 focus:ring-white/80"
            type="button"
            data-wifi-focus
            @click="cancelForget"
          >
            {{ wifiText('action.cancel') }}
          </button>
        </div>

        <div
          v-else
          class="mt-4 flex flex-col gap-3"
        >
          <p class="text-[14px] font-semibold">
            {{ state.phase === 'notOwner' ? wifiText('notOwner.title') : wifiText('unsupported.title') }}
          </p>
          <p class="text-[12px] leading-snug text-white/70">
            {{ state.phase === 'notOwner' ? wifiText('notOwner.body') : wifiText('unsupported.body') }}
          </p>
          <button
            class="w-full rounded-md border border-emerald-400/35 bg-emerald-500/20 py-2.5 text-[13px] font-semibold text-emerald-200 focus:outline-none focus:ring-2 focus:ring-white/80"
            type="button"
            data-wifi-focus
            data-wifi-primary
            @click="openAndroidSettings"
          >
            {{ wifiText('action.androidSettings') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
import type { TWifiCopyKey } from '@/helpers/wifiCopy'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useWifiProvisioning } from '@/composables/useWifiProvisioning'
import { androidRemoteKey } from '@/helpers/androidRemoteKey'
import { validateWifiPsk, validateWifiSsid } from '@/helpers/androidWifiProvisioning'
import { wifiText } from '@/helpers/wifiCopy'

const titleId = 'wifi-setup-sheet-title'
const rootEl = ref<HTMLElement | null>(null)
const {
  sheetOpen,
  state,
  statusSnap,
  unlockLine,
  successSeconds,
  closeSheet,
  beginEdit,
  backToEdit,
  setSsid,
  setPsk,
  toggleShowPsk,
  submit,
  askForget,
  cancelForget,
  confirmForget,
  openAndroidSettings
} = useWifiProvisioning()

const applySteps = computed(() => [
  wifiText('apply.step1'),
  wifiText('apply.step2'),
  wifiText('apply.step3'),
  wifiText('apply.step4')
])

const ssidError = computed(() =>
  state.value.phase === 'edit'
  && (state.value.showErrors || state.value.ssidDraft.length > 0)
  && validateWifiSsid(state.value.ssidDraft.trim()) != null
)

const pskError = computed(() =>
  state.value.phase === 'edit'
  && (state.value.showErrors || state.value.pskDraft.length > 0)
  && validateWifiPsk(state.value.pskDraft) != null
)

const pskCounter = computed(() => `${state.value.pskDraft.length} / 63`)

const connectedLine = computed(() => {
  const status = statusSnap.value
  if (!status) {
    return ''
  }
  const level = status.signalLevel
  const signal = level == null
    ? '—'
    : wifiText(`sheet.signal${Math.min(4, Math.max(0, Math.round(level)))}` as TWifiCopyKey)
  return wifiText('sheet.connectedDetail', { signal, ip: status.ipv4 ?? '—' })
})

const errorTitle = computed(() => {
  switch (state.value.errorReason) {
    case 'auth':
      return wifiText('error.auth')
    case 'notFound':
      return wifiText('error.notFound')
    case 'noInternet':
      return wifiText('error.noInternet')
    case 'dpcRefused':
      return wifiText('error.dpcRefused')
    case 'invalid':
      return wifiText('error.invalid')
    default:
      return wifiText('error.timeout')
  }
})

const unchangedLine = computed(() => {
  if (state.value.phase !== 'error' || state.value.errorReason === 'noInternet' || !state.value.savedSsid) {
    return ''
  }
  return wifiText('error.unchanged', { ssid: state.value.savedSsid })
})

function focusables(): HTMLElement[] {
  const root = rootEl.value
  if (!root) {
    return []
  }
  return Array.from(root.querySelectorAll<HTMLElement>('[data-wifi-focus]'))
    .filter(el => !el.hasAttribute('disabled'))
}

function moveFocus(delta: number): void {
  const items = focusables()
  if (items.length < 1) {
    return
  }
  const current = items.indexOf(document.activeElement as HTMLElement)
  const next = current < 0 ? 0 : (current + delta + items.length) % items.length
  items[next]?.focus()
}

function onKeydown(evt: KeyboardEvent): void {
  if (!sheetOpen.value) {
    return
  }
  const key = androidRemoteKey(evt)
  const typing = (evt.target as HTMLElement | null)?.tagName === 'INPUT'
  if (typing && (key === 'ArrowLeft' || key === 'ArrowRight')) {
    return
  }
  if (key !== 'ArrowUp' && key !== 'ArrowDown' && key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'Enter' && key !== 'NumpadEnter' && key !== 'Escape') {
    return
  }
  evt.preventDefault()
  evt.stopPropagation()
  if (key === 'Escape') {
    if (state.value.phase === 'applying') {
      return
    }
    if (typing) {
      (evt.target as HTMLElement).blur()
      return
    }
    closeSheet()
    return
  }
  if (key === 'Enter' || key === 'NumpadEnter') {
    if (typing) {
      const input = evt.target as HTMLInputElement
      if (input.name === 'psk') {
        void submit()
      }
      else {
        rootEl.value?.querySelector<HTMLInputElement>('input[name="psk"]')?.focus()
      }
      return
    }
    const current = document.activeElement as HTMLElement | null
    if (current && focusables().includes(current)) {
      current.click()
    }
    return
  }
  moveFocus(key === 'ArrowDown' || key === 'ArrowRight' ? 1 : -1)
}

async function focusPrimary(): Promise<void> {
  await nextTick()
  rootEl.value?.querySelector<HTMLElement>('[data-wifi-primary]')?.focus()
}

watch(sheetOpen, (open) => {
  if (open) {
    // Box D-pad/OK is injected on window. A document listener never sees it.
    window.addEventListener('keydown', onKeydown, true)
    void focusPrimary()
  }
  else {
    window.removeEventListener('keydown', onKeydown, true)
  }
})

watch(() => state.value.phase, () => {
  if (sheetOpen.value) {
    void focusPrimary()
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown, true)
})
</script>
